import { Component, OnInit, ViewChild, AfterViewInit } from "@angular/core";
import { Router } from "@angular/router";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { first, debounceTime } from "rxjs/operators";
import { MatLegacyInput as MatInput } from "@angular/material/legacy-input";
import { MatLegacyDialog as MatDialog, MatLegacyDialogConfig as MatDialogConfig } from "@angular/material/legacy-dialog";
import { RecaptchaComponent } from "ng-recaptcha";
import { throwError } from "rxjs";
import { Md5 } from "ts-md5/dist/md5"
import { ActivatedRoute } from '@angular/router';
import * as moment from "moment";
import * as _ from "lodash";

import { UserService, AuthenticationService, AlertService, GoogleAnalyticsService, LookupsService } from "../_services";
import { matchValidator } from "../_helpers/forms/match.validator";
import { AppGlobals } from "../app.globals";
import { RegisterDobConfirmationDialogComponent } from "./registerDobConfirmation";

@Component({
    templateUrl: "register.component.html",
    styleUrls: ["register.component.css"]
})
export class RegisterComponent implements OnInit, AfterViewInit {
    registerFormGroup: UntypedFormGroup;
    submitted = false;
    hide = true;
    hideConfirm = true;
    isProgram = false;
    genders: null;
    housingPrograms = [];
    requiresAdmissionsApplication = true;
    duplicateAccountChecked = false;

    @ViewChild("firstname") nameInput: MatInput;
    @ViewChild("programCode") codeInput: MatInput;
    @ViewChild("captchaRef") reCaptcha: RecaptchaComponent;

    constructor(private formBuilder: UntypedFormBuilder, private router: Router, private authenticationService: AuthenticationService, private userService: UserService, private lookupsService: LookupsService,
        private alertService: AlertService, private appGlobals: AppGlobals, public dialog: MatDialog, private googleAnalyticsService: GoogleAnalyticsService, private route: ActivatedRoute) {
        // redirect to home if already logged in
        if (this.authenticationService.currentUserValue) {
            this.router.navigate(["/"]);
        }
    }

    public captchaResponse: string = "";

    public resolved(captchaResponse: string) {
        const newResponse = captchaResponse
            ? `${captchaResponse.substr(0, 7)}...${captchaResponse.substr(-7)}`
            : captchaResponse;
        this.captchaResponse += `${JSON.stringify(newResponse)}\n`;
    }

    ngAfterViewInit() {
        setTimeout(() => {
            if (this.isProgram && this.codeInput)
                this.codeInput.focus();
            else
                this.nameInput.focus();
        }, 500);
    }

    ngOnInit() {
        this.appGlobals.pageTitleValue = "Register";

        this.route.paramMap.subscribe(params => {
            this.isProgram = params.get("program") === "program";
        });

        //genderTypes
        this.lookupsService.getGenderTypes()
            .pipe(first())
            .subscribe((response: any) => {
                this.genders = response.filter(f => f.description !== "Both") as any;
            });

        if (this.isProgram) {
            //housing programs
            this.lookupsService.getHousingProgramsByDate()
                .pipe(first())
                .subscribe((housingPrograms: any) => {
                    this.housingPrograms = _.filter(housingPrograms, f => f.isActive);
                });
        }

        this.initializeForm();
        this.onChanges();
    }

    initializeForm() {
        this.registerFormGroup = this.formBuilder.group({
            firstName: [null, [Validators.required, Validators.pattern(/^([\x20-\x7E])+$/)]],
            middleName: [null, [Validators.pattern(/^([\x20-\x7E])+$/)]],
            lastName: [null, [Validators.required, Validators.pattern(/^([\x20-\x7E])+$/)]],
            genderTypeId: [{ value: null, disabled: !this.isProgram || this.requiresAdmissionsApplication }, Validators.required],
            dateOfBirth: [{ value: null, disabled: !this.isProgram || this.requiresAdmissionsApplication }, Validators.required],
            programCode: [{ value: null, disabled: !this.isProgram }, Validators.required],
            eml: ["", [Validators.required, Validators.email]],
            confirmEml: ["", [Validators.required, Validators.email, matchValidator("eml", true)]],
            pass: ["", [Validators.required, Validators.pattern(/^(?=.*[A-Za-z])(?=.*\d)(?=.*[$@$!%*#?&])[A-Za-z\d$@$!%*#?&]{8,}$/)]],
            confirmPass: ["", [Validators.required, Validators.minLength(8), matchValidator("pass", true)]]
        });
    }

    onChanges() {
        //this.registerFormGroup.get("firstName").valueChanges
        //    .pipe(
        //        debounceTime(200)
        //    )
        //    .subscribe(firstName => {
        //        this.duplicateAccountChecked = false;
        //    });

        //this.registerFormGroup.get("lastName").valueChanges
        //    .pipe(
        //        debounceTime(200)
        //    )
        //    .subscribe(lastName => {
        //        this.duplicateAccountChecked = false;
        //    });
    }

    get registerFormControls() { return this.registerFormGroup.controls as any; }

    onSubmit(captchaResponse: any, submitBtn: HTMLButtonElement) {
        if (this.registerFormGroup.invalid) {
            return;
        }

        setTimeout(() => {
            submitBtn.disabled = true;
        }, 0);

        let housingProgram = null;

        if (this.isProgram)
            housingProgram = this.housingPrograms.find(f => f.program.code.toLowerCase() === this.registerFormControls.programCode.value.trim().toLowerCase());

        const data = {
            id: 0,
            firstName: this.registerFormControls.firstName.value,
            lastName: this.registerFormControls.lastName.value,
            email: this.registerFormControls.eml.value,
            confirmEmail: this.registerFormControls.confirmEml.value,
            password: Md5.hashStr(this.registerFormControls.pass.value),
            confirmPassword: Md5.hashStr(this.registerFormControls.confirmPass.value),
            middleName: !this.registerFormControls.middleName.value || !this.registerFormControls.middleName.value.trim() ? null : this.registerFormControls.middleName.value.trim(),
            housingProgramId: housingProgram ? housingProgram.programId : null,
            genderTypeId: this.registerFormControls.genderTypeId.value,
            dateOfBirth: this.registerFormControls.dateOfBirth.value ? moment(this.registerFormControls.dateOfBirth.value).format("YYYY-MM-DD") : null,
            currencyId: housingProgram ? housingProgram.currencyId : null,
            providerId: housingProgram ? housingProgram.providerId : null,
            funnelId: housingProgram ? housingProgram.funnelId : null,
            requiresAdmissionsApplication: housingProgram ? housingProgram.requiresAdmissionsApplication : true,
            schoolId: housingProgram ? housingProgram.program.schoolId : null,
            captcha: captchaResponse
        };

        this.userService.register(data)
            .pipe(first())
            .subscribe((requiresVerification: boolean) => {
                ////We call the event emitter function from our service and pass in the details
                //this.googleAnalyticsService.eventEmitter("Registration", "Registration Successful", "Registration", 1);

                this.googleAnalyticsService.logEvent("register_form_submit", "Registration", "Registration Successful", "Registration");

                this.router.navigate(["/login"]);

                if (requiresVerification)
                    this.alertService.showMessage("Registration Successful. Please verify your account with the verification code sent to you via email", "Registration", this.appGlobals.alertMessageTypes.success, 6000);
            }, error => {
                this.reCaptcha.reset();
                throwError(error);
            });
    }

    checkForDuplicateAccount() {
        if (!this.duplicateAccountChecked) {
            const firstName = this.registerFormControls.firstName.value;
            const lastName = this.registerFormControls.lastName.value;

            if (!this.isProgram) {
                if (firstName && lastName) {
                    this.userService.getStudentRegistrationsByName(firstName, lastName)
                        .pipe(first())
                        .subscribe((result: any) => {
                            if (result && result.length > 0) {
                                const dialogConfig = new MatDialogConfig();

                                dialogConfig.disableClose = true;
                                dialogConfig.autoFocus = true;
                                dialogConfig.closeOnNavigation = true;
                                dialogConfig.height = "auto";
                                dialogConfig.panelClass = "popup-dialog-container";

                                dialogConfig.data = {
                                    studentRegistrations: result
                                };

                                this.duplicateAccountChecked = true;

                                const dialogRef = this.dialog.open(RegisterDobConfirmationDialogComponent, dialogConfig);

                                dialogRef.afterClosed().subscribe(val => {
                                    this.duplicateAccountChecked = false;

                                    if (val) {
                                        setTimeout(() => {
                                            this.registerFormGroup.reset({ emitEvent: false });

                                            this.nameInput.focus();
                                        }, 0);
                                    }
                                });
                            }
                        });
                }
            } else {
                if (!this.registerFormGroup.get("programCode").errors && firstName && lastName && this.registerFormControls.eml.value && this.registerFormControls.dateOfBirth.value && this.registerFormControls.programCode.value) {
                    this.userService.getStudentRegistrationsByProgramCode(firstName, lastName, this.registerFormControls.eml.value, moment(this.registerFormControls.dateOfBirth.value).format("YYYY-MM-DD"), this.registerFormControls.programCode.value)
                        .pipe(first())
                        .subscribe((result: any) => {
                            if (result) {
                                this.alertService.showMessage(`An account with the same details already exists. The login email for this account is ${result.email}. Please contact the admissions office if this is not your account.`, "Registration", this.appGlobals.alertMessageTypes.error);

                                this.duplicateAccountChecked = true;
                            } else
                                this.duplicateAccountChecked = false;
                        });
                }
            }
        }
    }

    checkProgramCode() {
        const program = this.housingPrograms.find(f => this.registerFormControls.programCode.value && f.program.code.toLowerCase() === this.registerFormControls.programCode.value.trim().toLowerCase());

        if (this.registerFormControls.programCode.value && !program) {
            this.registerFormGroup.get("programCode").setErrors({ "notFound": true });
            this.requiresAdmissionsApplication = true;
        } else
            this.requiresAdmissionsApplication = program ? program.requiresAdmissionsApplication : true;

        if (!this.requiresAdmissionsApplication) {
            this.registerFormGroup.get("genderTypeId").enable();
            this.registerFormGroup.get("dateOfBirth").enable();
        } else {
            this.registerFormGroup.get("genderTypeId").disable();
            this.registerFormGroup.get("dateOfBirth").disable();
        }
    }

    trimValue(controlName: string) {
        if (this.registerFormGroup.controls[controlName].value)
            this.registerFormGroup.controls[controlName].setValue(this.registerFormGroup.controls[controlName].value.trim());
    }
}
